import { useEffect, useState } from 'react';
import {
  CampaignType_Enum as CampaignType,
  Status_Enum as Status,
} from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import { useGetAudience, useGetCampaignUsage } from '@frontend/api-bulk-messaging';
import { MediaQueries } from '@frontend/api-media';
import { useTranslation } from '@frontend/i18n';
import { useOrgLocationTree } from '@frontend/org-location-picker';
import { useAppScopeStore } from '@frontend/scope';
import { SettingsSection } from '@frontend/settings-ui';
import { BulkEmailPrefixes } from '@frontend/tracking-prefixes';
import { theme } from '@frontend/theme';
import {
  Button,
  ButtonBar,
  CheckboxField,
  FormRow,
  Modal,
  SpinningLoader,
  StepStatus,
  Stepper,
  Text,
  useAlert,
  useModalControl,
} from '@frontend/design-system';
import { useBulkMessageEditorContext } from '../../bulk-message-editor.context';
import { DATE_TIME_SECOND_FORMAT } from '../../constants';
import { useAttachmentStep } from '../../hooks/use-attachment-step';
import { useAudienceManagement } from '../../hooks/use-audience-management';
import { useAudienceStep } from '../../hooks/use-audience-step';
import { useBulkNavigator } from '../../hooks/use-bulk-navigator';
import { useBulkUrlData } from '../../hooks/use-bulk-url-data';
import { useDraft } from '../../hooks/use-draft';
import { useEmailContentStep } from '../../hooks/use-email-content-step';
import { useEmailForm } from '../../hooks/use-email-form';
import { useHandleSaveError } from '../../hooks/use-handle-save-error';
import { useLocationStep } from '../../hooks/use-location-step';
import { useMonthUsage } from '../../hooks/use-month-usage';
import { useOrgLevelLocationSelector } from '../../hooks/use-org-level-location-selector';
import { usePassedRecipients } from '../../hooks/use-passed-recipients';
import { useTextContentStep } from '../../hooks/use-text-content-step';
import { useTitleStep } from '../../hooks/use-title-step';
import { useWhenToSendStep } from '../../hooks/use-when-to-send-step';
import { SendType, StepId } from '../../types';
import { checkIsUnscheduled } from '../../utils/check-is-unscheduled';
import { getUniqueItems } from '../../utils/get-unique-items';
import { sortItems } from '../../utils/sort-items';
import { BulkMessageSegmentedBar } from '../bulk-message-segmented-bar';
import { EmailAddresses } from './email-addresses';
import { MessageCount } from './message-count';

dayjs.extend(customParseFormat);
dayjs.extend(utc);

const CAN_SAVE_AS_DRAFT_STATUSES = [Status.UNSPECIFIED, Status.DRAFT, Status.HIDDEN_DRAFT, Status.SCHEDULED];

// TODO: Break this massive component up into smaller hooks, components, and chunks
export const CampaignEditor = () => {
  const { t } = useTranslation('bulk-messaging');
  const alert = useAlert();

  const {
    activeStep,
    attachmentMediaIds,
    audienceCount,
    campaignId,
    campaignType,
    currentStatus,
    enableValidation,
    getCampaignData,
    initialCampaign,
    locationIds,
    messageCount,
    messageTemplate,
    readOnly,
    saveAs,
    setActiveStep,
    setAudienceCount,
    setEnableValidation,
    setMessageCount,
    subject,
    templateJson,
    unsavedChangesProps,
  } = useBulkMessageEditorContext();
  const { setChangesExist } = unsavedChangesProps;
  const { formProps, seedValues, validateForm, values } = useEmailForm();

  const { selectedOrgId: orgId } = useAppScopeStore();
  const { permission: locationPickerPermission } = useOrgLevelLocationSelector(campaignType);
  const { accessibleCount } = useOrgLocationTree({ permission: locationPickerPermission });

  const { subNav } = useBulkUrlData();
  const navigateTo = useBulkNavigator(campaignType);
  const isEmailCampaign = campaignType === CampaignType.EMAIL;

  const isUnscheduled = checkIsUnscheduled(currentStatus);

  const {
    isFetching: isFetchingMonthUsage,
    campaignMetrics,
    month,
    monthMetrics,
  } = useMonthUsage({
    messageCount,
    campaign: initialCampaign,
    campaignType,
    locationIds: formProps.locationIds.value,
    selectedDate: values.date ?? '',
  });

  const { allotment: planned, remaining: remainingMessages } = campaignMetrics;

  useEffect(() => {
    const uniqueLocationIdsToAdd = getUniqueItems(formProps.locationIds.value, locationIds);
    if (uniqueLocationIdsToAdd?.length) {
      seedValues({ locationIds: uniqueLocationIdsToAdd });
    }
  }, [sortItems([...locationIds]).join(',')]);

  // For various reasons, this validation is better handled outside of the useForm hook
  const sendIntervalError =
    values.sendOptions === SendType.SendSpecific &&
    (formProps.sendInterval.error || (values.sendInterval === '' && formProps.sendInterval.touched))
      ? formProps.sendInterval.error || t('This field is required')
      : '';

  // Show the step when the user can pick >1 location OR the campaign already targets >1 — otherwise a
  // scheduled campaign whose attached locations the user can no longer access would hide the step entirely.
  const showLocationsSelectorStep = accessibleCount > 1 || (initialCampaign?.locationIds?.length ?? 0) > 1;

  // Attachments are only supported in single-location email campaigns
  const hideAttachmentStep = formProps.locationIds.value.length > 1 || !isEmailCampaign;
  const firstSelectedLocationId = formProps.locationIds.value[0];
  const { data: allAttachments = [] } = MediaQueries.useGetAttachmentMedia(
    firstSelectedLocationId ? [firstSelectedLocationId] : undefined
  );

  const getSelectorSteps = () => {
    const steps: StepStatus = {
      1: 'active',
      2: 'inactive',
      3: 'inactive',
      4: 'inactive',
    };

    if (showLocationsSelectorStep) {
      steps[5] = 'inactive';
    }
    if (!hideAttachmentStep) {
      steps[6] = 'inactive';
    }
    return steps;
  };

  const [stepStatus, setStepStatus] = useState<StepStatus>(getSelectorSteps());

  const { handleSaveError } = useHandleSaveError();
  const { createHiddenDraft, isSavingDraft, saveDraft } = useDraft();

  const cancel = () => {
    if (subNav === 'drafts') {
      navigateTo.drafts();
    } else {
      navigateTo.campaigns();
    }
  };

  const isEditorComplete = () => {
    const isEmailFieldsComplete = () => {
      return !!templateJson && !!subject;
    };

    const isTextFieldsComplete = () => {
      return !!messageTemplate;
    };

    const dateTimeValue = dayjs(`${values.date} ${values.time}`, DATE_TIME_SECOND_FORMAT);
    const isDateTimeComplete =
      !formProps.date.error && !formProps.time.error && dateTimeValue.isValid() && dateTimeValue.isAfter(dayjs());

    const isSendIntervalComplete =
      values.sendOptions === SendType.SendAll || (!formProps.sendInterval.error && values.sendInterval !== '');

    const isDaysToSendComplete =
      values.sendOptions === SendType.SendAll || (!formProps.daysToSend.error && values.daysToSend?.length !== 0);

    const isCommonFieldsComplete =
      (readOnly && !formProps.title.error) ||
      (!readOnly &&
        !formProps.title.error &&
        !isLocationIdsError &&
        isDateTimeComplete &&
        isSendIntervalComplete &&
        isDaysToSendComplete &&
        !!audienceCount &&
        remainingMessages >= 0 &&
        !formProps.consent.error);

    const isRemainingFieldsComplete = isEmailCampaign ? isEmailFieldsComplete() : isTextFieldsComplete();

    return isCommonFieldsComplete && isRemainingFieldsComplete;
  };

  const scheduleCampaign = async () => {
    try {
      const newCampaign = getCampaignData();
      validateEditor();

      if (!isEditorComplete()) return;

      if (!campaignId) {
        alert.error(t('An unexpected error occurred. Please try again or contact support.'));
        console.error(
          'Cannot schedule campaign. A campaign ID has somehow not been set and yet there is an audience count. How on earth has this happened?'
        );
        return;
      }
      // reset unsaved changes state specifically for duplicate mode
      setChangesExist(false);
      setIsSchedulingCampaign(true);
      // status to convert to when the `Update Campaign` button is clicked
      // certain statuses cannot be converted back to `SCHEDULED`
      const saveAsStatus = isUnscheduled ? Status.SCHEDULED : currentStatus;
      await saveAs(newCampaign, saveAsStatus);
      isEmailCampaign
        ? alert.success(isUnscheduled ? t('Email campaign scheduled!') : t('Email campaign updated!'))
        : alert.success(isUnscheduled ? t('Text campaign scheduled!') : t('Text campaign updated!'));
      navigateTo.campaigns();
    } catch (error) {
      handleSaveError(
        error,
        isUnscheduled
          ? t('An unexpected error occurred while scheduling your campaign. Please try again or contact support.')
          : t('An unexpected error occurred while updating your campaign. Please try again or contact support.')
      );
    } finally {
      setIsSchedulingCampaign(false);
    }
  };

  const openNewComposer = () => {
    navigateTo.new();
  };

  const openEditComposer = () => {
    navigateTo.composer();
  };

  const [isSchedulingCampaign, setIsSchedulingCampaign] = useState(false);

  const { deleteAudience, deleteSegments, isProcessingAudience, segmentCount, upsertAudience } =
    useAudienceManagement();

  const { data: audience, isFetching: isFetchingAudience } = useGetAudience({
    campaignId,
    orgId,
  });

  useEffect(() => {
    setAudienceCount(audience?.destinations ?? 0);
  }, [audience?.destinations, setAudienceCount]);

  const { data: campaignUsage, isFetching: isFetchingCampaignUsage } = useGetCampaignUsage(
    {
      campaignId,
      orgId,
      updatedMessageTemplate: campaignType === CampaignType.SMS ? messageTemplate : undefined,
    },
    {
      enabled: !!campaignId && !!messageTemplate && !!audienceCount,
    }
  );

  useEffect(() => {
    setMessageCount(campaignUsage?.total ?? 0);
  }, [campaignUsage?.total, setMessageCount]);

  const isFetchingMessageCount =
    isFetchingAudience || isFetchingCampaignUsage || isFetchingMonthUsage || isProcessingAudience;

  const validateEditor = () => {
    setEnableValidation(true);
    validateForm();
  };

  const getContentError = () => {
    if (!enableValidation) return '';
    if (isEmailCampaign) {
      if (!templateJson) {
        return t('Email content is required');
      }

      if (!subject) {
        return t('A subject is required');
      }
    }

    if (!isEmailCampaign) {
      if (!messageTemplate) {
        return t('Text content is required');
      }
    }

    return '';
  };

  const contentError = getContentError();
  const audienceError = enableValidation && !audienceCount ? t('An audience is required') : '';
  const messageCountError =
    enableValidation && remainingMessages < 0
      ? isEmailCampaign
        ? t('Too many emails will be used')
        : t('Too many messages will be used')
      : '';
  const isTitleError = formProps.title.touched && !!formProps.title.error;
  const isDateError = formProps.date.touched && !!formProps.date.error;
  const isTimeError = formProps.time.touched && !!formProps.time.error;
  const isDaysToSendError =
    values.sendOptions === SendType.SendSpecific &&
    (!!formProps.daysToSend.error || (values.daysToSend?.length === 0 && formProps.daysToSend.touched));
  const isLocationIdsError = showLocationsSelectorStep ? !!formProps.locationIds.error : false;
  // Check the list of available media and make sure that it's available for the selected location
  const missingAttachments = (attachmentMediaIds || [])?.filter(
    (id) => !allAttachments?.find((media) => media.ID === id)
  );
  const isAttachmentError = attachmentMediaIds ? !!missingAttachments.length : false;
  const isConsentError = formProps.consent.touched && !!formProps.consent.error;

  const editorError =
    enableValidation && !isEditorComplete()
      ? isUnscheduled
        ? t('Your campaign needs to be adjusted before it can be scheduled')
        : t('Your campaign needs to be adjusted before it can be updated')
      : '';

  const [touched, setTouched] = useState<Record<StepId, boolean>>({
    title: false,
    locations: false,
    'when-to-send': false,
    content: false,
    audience: false,
    attachments: false,
  });
  const attachmentsIndex = showLocationsSelectorStep ? 5 : 4;

  const updateStepperStatus = () => {
    if (readOnly) {
      if (showLocationsSelectorStep && !hideAttachmentStep) {
        setStepStatus({ 1: 'active', 2: 'completed', 3: 'completed', 4: 'completed', 5: 'completed', 6: 'completed' });
      } else if (showLocationsSelectorStep) {
        setStepStatus({ 1: 'active', 2: 'completed', 3: 'completed', 4: 'completed', 5: 'completed' });
      } else {
        setStepStatus({ 1: 'active', 2: 'completed', 3: 'completed', 4: 'completed' });
      }
      return;
    }

    const errors: boolean[] = [
      isTitleError,
      isDateError || isTimeError || isDaysToSendError || !!sendIntervalError,
      !!contentError,
      !!audienceError,
    ];
    if (showLocationsSelectorStep) {
      errors.splice(1, 0, !!formProps.locationIds.error);
    }
    if (!hideAttachmentStep) {
      errors.push(isAttachmentError);
    }

    const isSendOptionsPopulated =
      values.sendOptions === SendType.SendSpecific ? !!values.sendInterval && !!values.daysToSend : true;
    const populated: boolean[] = [
      !!values.title,
      !!values.date && !!values.time && isSendOptionsPopulated,
      !!messageTemplate,
      !!audienceCount,
    ];
    if (showLocationsSelectorStep) {
      populated.splice(1, 0, !!values.locationIds);
    }
    if (!hideAttachmentStep) {
      populated.push(!!attachmentMediaIds);
    }

    const newStepStatus: StepStatus = {};
    errors.forEach((error, index) => {
      const key = index + 1;
      if (key === activeStep) {
        if (error) {
          newStepStatus[key] = 'errorActive';
        } else {
          newStepStatus[key] = 'active';
        }
      } else {
        if (error) {
          newStepStatus[key] = 'error';
        } else if (populated[index] && index === attachmentsIndex) {
          newStepStatus[key] = touched.attachments ? 'completed' : 'inactive';
        } else if (populated[index]) {
          newStepStatus[key] = 'completed';
        } else {
          newStepStatus[key] = 'inactive';
        }
      }
    });

    setStepStatus(newStepStatus);
  };

  useEffect(() => {
    updateStepperStatus();
  }, [
    activeStep,
    enableValidation,
    isTitleError,
    isDateError,
    isTimeError,
    isDaysToSendError,
    sendIntervalError,
    contentError,
    audienceError,
    isLocationIdsError,
    // The location-selector gate can flip after mount (org-level wetag + scope tree resolve asynchronously),
    // which adds/removes the locations + attachments steps — recompute the status map when it does.
    showLocationsSelectorStep,
    hideAttachmentStep,
  ]);

  useEffect(() => {
    if (enableValidation) {
      validateForm();
    }
  }, [enableValidation]);

  const getMaxStep = () => {
    let maxStep = 4;
    if (showLocationsSelectorStep) maxStep += 1;
    if (!hideAttachmentStep) maxStep += 1;
    return maxStep;
  };
  const maxStep = getMaxStep();

  const next = (stepId: StepId) => {
    const nextStep = Math.min(activeStep + 1, maxStep);
    setActiveStep(nextStep);
    const nextStepIdIndex = steps.findIndex((step) => step.id === stepId) + 1;
    const nextStepId = steps[nextStepIdIndex]?.id;
    if (nextStepId) setTouched({ ...touched, [nextStepId]: true });
  };

  const previous = (stepId: StepId) => {
    const previousStep = Math.max(activeStep - 1, 1);
    setActiveStep(previousStep);
    const previousStepIdIndex = steps.findIndex((step) => step.id === stepId) - 1;
    const previousStepId = steps[previousStepIdIndex]?.id;
    if (previousStepId) setTouched({ ...touched, [previousStepId]: true });
  };

  const getValidStepNumber = (step: number): number => {
    if (step > maxStep) return maxStep;
    if (step < 1) return 1;
    return step;
  };

  const skipTo = (step: number, stepId: StepId) => {
    if (step === activeStep) return;
    const validatedStep = getValidStepNumber(step);
    setActiveStep(validatedStep);
    setTouched({ ...touched, [stepId]: true });
  };

  const stepperControlProps = {
    next,
    previous,
  };

  const titleStep = useTitleStep({ ...stepperControlProps, fieldProps: formProps.title });

  const locationStep = useLocationStep({
    ...stepperControlProps,
    hide: !showLocationsSelectorStep,
    locationIdsFieldProps: formProps.locationIds,
  });

  const whenToSendStep = useWhenToSendStep({
    ...stepperControlProps,
    fieldProps: {
      date: formProps.date,
      daysToSend: formProps.daysToSend,
      sendInterval: formProps.sendInterval,
      sendOptions: formProps.sendOptions,
      time: formProps.time,
    },
    sendIntervalError,
  });

  const emailContentStep = useEmailContentStep({
    contentError,
    openEditComposer,
    openNewComposer,
    ...stepperControlProps,
  });

  const textContentStep = useTextContentStep({
    contentError,
    ...stepperControlProps,
  });

  const contentStep = isEmailCampaign ? emailContentStep : textContentStep;

  const audienceStep = useAudienceStep({
    ...stepperControlProps,
    createHiddenDraft,
    deleteAudience,
    deleteSegments,
    duplicateCount: audience?.duplicateRecipientCount ?? 0,
    isCountingAudience: isFetchingAudience,
    isProcessingAudience,
    locationIds: formProps.locationIds.value,
    next: !hideAttachmentStep ? next : undefined,
    segmentCount,
    upsertAudience,
  });

  const attachmentStep = useAttachmentStep({
    ...stepperControlProps,
    allAttachments,
    fieldProps: formProps.attachments,
    hide: hideAttachmentStep,
    missingAttachments,
    selectedLocationId: firstSelectedLocationId,
  });

  const steps = [titleStep, locationStep, whenToSendStep, contentStep, audienceStep, attachmentStep].filter(
    (item) => !item.hide
  );

  const { modalProps: messageCountModalProps, triggerProps: triggerMessageCountProps } = useModalControl();

  const { isProcessingPassedRecipients } = usePassedRecipients({ upsertAudience, createHiddenDraft });

  if (isProcessingPassedRecipients) {
    return (
      <div css={{ display: 'flex', justifyContent: 'center', marginTop: 40 }}>
        <SpinningLoader />
      </div>
    );
  }

  return (
    <>
      {/** Since we are using similar metric names, its better to clearly state the context of the metric eg: campaignMetrics.used vs monthMetrics.used */}
      <BulkMessageSegmentedBar
        additional={planned}
        forecastedUsage={campaignMetrics.used + campaignMetrics.pending + planned}
        month={month}
        pending={campaignMetrics.pending}
        quota={monthMetrics.allotment}
        sent={campaignMetrics.used}
        showAllotments={showLocationsSelectorStep}
        used={monthMetrics.used}
      />
      {readOnly ? (
        <div
          css={{ display: 'flex', flexDirection: 'column', rowGap: theme.spacing(2), marginBottom: theme.spacing(6) }}
        >
          {steps.map((step, index) => (
            <SettingsSection key={index}>{step.readValue ?? step.collapsedValue}</SettingsSection>
          ))}
        </div>
      ) : (
        <Stepper
          stepStatus={stepStatus}
          shouldScrollToCard={false}
          css={{ maxWidth: 'initial', marginRight: 0, marginBottom: theme.spacing(6) }}
        >
          {steps.map((step, index) => (
            <Stepper.Card
              key={index}
              css={StepperCardStyles(readOnly)}
              onClick={readOnly ? undefined : (stepNumber) => skipTo(stepNumber, step.id)}
              preventDefaultOnClick
              stepValue={step.collapsedValue}
            >
              {step.Component}
            </Stepper.Card>
          ))}
        </Stepper>
      )}
      <section css={{ display: 'flex', marginBottom: theme.spacing(3), flexWrap: 'wrap', gap: theme.spacing(2) }}>
        <MessageCount count={planned} isError={!!messageCountError} isLoading={isFetchingMessageCount}>
          {isEmailCampaign ? (
            t('Emails Will Be Sent')
          ) : (
            <>
              {t('Messages Will Be Sent')}
              <Button
                {...triggerMessageCountProps}
                css={{
                  color: 'inherit',
                  position: 'absolute',
                  marginLeft: '2px',
                  marginTop: '-10px',
                  '.button-icon': { color: 'inherit' },
                }}
                iconName='info'
                trackingId={`${BulkEmailPrefixes.Editor}-message-count-tooltip`}
                variant='secondary'
              />
            </>
          )}
        </MessageCount>
        <MessageCount count={remainingMessages} isError={!!messageCountError} isLoading={isFetchingMessageCount}>
          {isEmailCampaign ? t('Emails Remaining After Send') : t('Messages Remaining After Send')}
        </MessageCount>
      </section>
      {messageCountError && (
        <Text color='error' size='small' css={{ margin: theme.spacing(-1, 0, 3, 0) }}>
          {messageCountError}
        </Text>
      )}
      {isEmailCampaign && (
        <EmailAddresses
          css={{ marginTop: theme.spacing(3), marginBottom: theme.spacing(3) }}
          locationIds={formProps.locationIds.value}
        />
      )}
      <FormRow>
        <CheckboxField
          css={{
            div: {
              alignSelf: 'center',
            },
            label: {
              fontSize: theme.font.size.small,
              color: isConsentError ? theme.font.colors.error : theme.font.colors.default,
            },
          }}
          {...formProps.consent}
          label={
            isEmailCampaign
              ? t(
                  "Several laws in the US and Canada require that emails sent by businesses to consumers are appropriate and are sent only after receiving the individual's consent. By checking this box, you confirm that the recipients of this email have opted-in to receive emails from your business."
                )
              : t(
                  'Several laws in the US and Canada, as well as standards set by the wireless communications industry, require businesses that send text messages to consumers first have explicit, written consent from the recipient. Failure to comply with consent requirements can result in your message traffic being blocked. By checking this box, you confirm that you have obtained written consent from each recipient to receive marketing and promotional messages from your business.'
                )
          }
          trackingId={`${BulkEmailPrefixes.Editor}-consent-checkbox`}
        />
      </FormRow>
      {isConsentError && (
        <Text color='error' size='small' css={{ margin: theme.spacing(-1, 0, 3, 0) }}>
          {formProps.consent.error}
        </Text>
      )}
      <ButtonBar css={{ justifyContent: 'space-between', padding: 0 }}>
        <div css={{ display: 'flex', alignItems: 'center' }}>
          <Button
            size='large'
            onClick={scheduleCampaign}
            trackingId={`${BulkEmailPrefixes.Editor}-schedule-campaign-btn`}
            css={{ marginRight: theme.spacing(1) }}
            disabled={isFetchingMessageCount || isSchedulingCampaign}
          >
            {isSchedulingCampaign && <SpinningLoader size='xs' css={{ marginRight: theme.spacing(1) }} />}
            {isUnscheduled ? t('Create Campaign') : t('Update Campaign')}
          </Button>
          {CAN_SAVE_AS_DRAFT_STATUSES.includes(currentStatus) && (
            <Button
              size='large'
              variant='secondary'
              onClick={saveDraft}
              trackingId={`${BulkEmailPrefixes.Editor}-save-draft-btn`}
              disabled={isFetchingMessageCount || isSavingDraft}
            >
              {isSavingDraft && <SpinningLoader size='xs' css={{ marginRight: theme.spacing(1) }} />}
              {t('Save as Draft')}
            </Button>
          )}
        </div>
        <Button size='large' variant='tertiary' onClick={cancel} trackingId={`${BulkEmailPrefixes.Editor}-cancel-btn`}>
          {t('Cancel')}
        </Button>
      </ButtonBar>
      {editorError && (
        <Text color='error' size='small' css={{ marginTop: theme.spacing(1) }}>
          {editorError}
        </Text>
      )}
      <Modal maxWidth={700} {...messageCountModalProps}>
        <Modal.Header>{t('How Are Messages Counted?')}</Modal.Header>
        <Modal.Body>
          <Text
            css={{
              marginBottom: theme.spacing(2),
            }}
          >
            {t(
              'Total messages used comes from the number of recipients and how long the message is, based on character counts. The number of characters per message varies somewhat, but is roughly 150-160 characters.'
            )}
          </Text>
          <Text>
            {t(
              'Additionally, if you use a tag - for example, first name or last name - some recipients may have a long name that pushes their message over the threshold and will consume an extra message.'
            )}
          </Text>
        </Modal.Body>
        <Modal.Footer
          primary={{
            label: t('Close'),
            onClick: () => true,
          }}
        ></Modal.Footer>
      </Modal>
    </>
  );
};

const StepperCardStyles = (readOnly: boolean) => ({
  '&[aria-current="false"]': {
    cursor: readOnly ? 'not-allowed' : 'pointer',
    height: 'fit-content !important', // This is needed since the height is a style prop
    '.step-content-title': {
      overflow: 'initial',
      whiteSpace: 'wrap',
    },
  },
  '.step-content-title': {
    height: 'fit-content',
  },
});
